import { OfferActionSurface } from '../../../../../../services/analytics/marketplaceEvents';
import { ContactOfferTimelineEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactOfferTimelineEntry';

export type MarketplaceOffer = ContactOfferTimelineEntry;

export type OfferCardView = {
  offer: MarketplaceOffer;
  /** The pending driver_counter — non-null when driver is in State C (waiting). */
  driverCounter: MarketplaceOffer | null;
  dealerLabel: string;
  amount: number;
  /** Non-null when the driver has countered and is waiting for the dealer's response (State C). */
  driverCounterAmount: number | null;
  /** Non-null when the dealer is countering back to the driver's earlier counter */
  respondedToCounterAmount: number | null;
  /** Non-null when this offer revived a thread after the dealer rejected the driver's earlier counter. */
  revivedRejectedCounterAmount: number | null;
  isTopOffer: boolean;
  /** Every money event for this dealer in API order — the conversation timeline's raw stream. */
  history: MarketplaceOffer[];
};

export type InactiveCardView = {
  offer: MarketplaceOffer;
  dealerLabel: string;
  amount: number;
  /** The driver's rejected counter amount — non-null only for 'rejected' (State E). */
  driverCounterAmount: number | null;
  inactiveState: 'rejected' | 'withdrawn' | 'expired' | 'closed';
  inactiveAt: string;
  /** Every money event for this dealer in API order — the conversation timeline's raw stream. */
  history: MarketplaceOffer[];
};

export type OfferCards = {
  activeCards: OfferCardView[];
  inactiveCards: InactiveCardView[];
};

export type OfferActionKind = 'accept' | 'counter' | 'adjust' | 'withdraw';

// A driver action captured at the moment its button is tapped. The card is snapshotted here so
// the modal keeps rendering it even after a background poll drops the card from the active list.
export type OfferAction = {
  kind: OfferActionKind;
  card: OfferCardView;
  surface: OfferActionSurface;
};

export const hasNonDriverAskOffer = (offers?: MarketplaceOffer[]): boolean =>
  offers?.some((offer) => offer.type !== 'driver_ask') ?? false;

// Reports whether the live offer (matched by id from the latest poll) has drifted from the offer
// a modal opened on — gone, re-statused, or re-priced. The captured card is already frozen at
// action time (see OfferActionModals), so `openOffer` IS the open-time snapshot; we just compare
// it against the freshest poll. Drives the proactive "this offer changed" banner.
export const isOfferStale = (
  openOffer: MarketplaceOffer | undefined,
  liveOffers: MarketplaceOffer[],
): boolean => {
  if (openOffer == null) return false;

  const live = liveOffers.find((offer) => offer.id === openOffer.id);
  if (live == null) return true;

  return (
    live.status !== openOffer.status ||
    live.amount !== openOffer.amount ||
    live.updatedAt !== openOffer.updatedAt
  );
};

// Pulls the server's `{ error }` message off a failed marketplace request (the client throws
// an Error with the parsed body attached). Lets a modal show the real reason (e.g. the LE-18255
// ceiling rejection) inline instead of a generic toast. Null when there's no usable message.
export const getOfferErrorMessage = (error: unknown): string | null => {
  const body = (error as { body?: unknown } | null)?.body;
  if (body != null && typeof body === 'object' && 'error' in body) {
    const { error: message } = body as { error: unknown };
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return null;
};

const byCreatedDescending = (first: MarketplaceOffer, second: MarketplaceOffer) =>
  second.createdAt.localeCompare(first.createdAt);

// A driver's own retracted counter is a no-op — the ball is back in the driver's court,
// so we look through it to the dealer offer beneath rather than treating it as the state.
const isDriverRetraction = (offer: MarketplaceOffer) =>
  offer.type === 'driver_counter' && (offer.status === 'withdrawn' || offer.status === 'expired');

const byAmountDescending = (
  first: { amount: number; offer: MarketplaceOffer },
  second: { amount: number; offer: MarketplaceOffer },
): number =>
  second.amount !== first.amount
    ? second.amount - first.amount
    : first.offer.createdAt.localeCompare(second.offer.createdAt);

const bySoonestExpiring = (
  first: { amount: number; offer: MarketplaceOffer },
  second: { amount: number; offer: MarketplaceOffer },
): number => {
  const firstExpiry = first.offer.expiresAt;
  const secondExpiry = second.offer.expiresAt;
  if (firstExpiry !== secondExpiry) {
    if (firstExpiry == null) return 1;
    if (secondExpiry == null) return -1;
    return firstExpiry.localeCompare(secondExpiry);
  }
  return byAmountDescending(first, second);
};

const byMostRecentlyInactive = (first: InactiveCardView, second: InactiveCardView): number =>
  second.inactiveAt.localeCompare(first.inactiveAt);

// Maps a terminal offer's `${side}:${status}` to its inactive card state. State E = dealer rejected
// the driver's counter; State F = dealer withdrew; expired = TTL elapsed before the driver
// responded; outbid/superseded = closed because the driver accepted a different dealer. Any
// unmapped outcome (accepted / countered / edited, etc.) is not rendered.
const INACTIVE_STATE_BY_OUTCOME: Record<string, InactiveCardView['inactiveState']> = {
  'driver:rejected': 'rejected',
  'dealer:withdrawn': 'withdrawn',
  'dealer:expired': 'expired',
  'dealer:outbid': 'closed',
  'driver:superseded': 'closed',
};

export const getOfferCards = (offers?: MarketplaceOffer[]): OfferCards => {
  const allOffers = offers ?? [];
  const byId = new Map(allOffers.map((offer) => [offer.id, offer]));
  const parentOf = (offer: MarketplaceOffer) =>
    offer.parentOfferId != null ? byId.get(offer.parentOfferId) : undefined;

  const byOrg = new Map<string, MarketplaceOffer[]>();
  allOffers
    .filter((offer) => offer.type !== 'driver_ask' && offer.orgName != null)
    .forEach((offer) => {
      const orgName = offer.orgName as string;
      const group = byOrg.get(orgName);
      if (group) group.push(offer);
      else byOrg.set(orgName, [offer]);
    });

  const activeCards: Omit<OfferCardView, 'isTopOffer'>[] = [];
  const inactiveCards: InactiveCardView[] = [];

  byOrg.forEach((orgOffers, orgName) => {
    // Captured before the state machine so every card carries its dealer's full raw stream.
    const history = orgOffers.filter((offer) => offer.amount > 0);
    const sortedDescending = [...orgOffers].sort(byCreatedDescending);

    // Has Pending Offer
    const pending = sortedDescending.find((offer) => offer.status === 'pending');
    if (pending != null) {
      if (pending.type === 'driver_counter') {
        // State C — driver countered, waiting on the dealer.
        const parent = parentOf(pending);
        if (parent == null || parent.amount <= 0) return;
        activeCards.push({
          offer: parent,
          driverCounter: pending,
          dealerLabel: orgName,
          amount: parent.amount,
          driverCounterAmount: pending.amount,
          respondedToCounterAmount: null,
          revivedRejectedCounterAmount: null,
          history,
        });
      } else if (pending.amount > 0) {
        // State A / B / revival — pending dealer-side offer. When it's a dealer_counter, its parent
        // is the driver's own counter: 'countered' means the dealer is responding to a live counter
        // (State B), 'rejected' means the dealer went dark and is now reviving the thread.
        const parent = parentOf(pending);
        const parentCounter =
          pending.type === 'dealer_counter' &&
          parent?.type === 'driver_counter' &&
          parent.amount > 0
            ? parent
            : null;
        activeCards.push({
          offer: pending,
          driverCounter: null,
          dealerLabel: orgName,
          amount: pending.amount,
          driverCounterAmount: null,
          respondedToCounterAmount:
            parentCounter?.status === 'countered' ? parentCounter.amount : null,
          revivedRejectedCounterAmount:
            parentCounter?.status === 'rejected' ? parentCounter.amount : null,
          history,
        });
      }
      return;
    }

    // No pending offer — the thread is terminal. Look through the driver's own retracted
    // counters to find the dealer-side offer that determines the inactive state.
    const controlling = sortedDescending.find((offer) => !isDriverRetraction(offer));
    if (controlling == null) return;

    const side = controlling.type === 'driver_counter' ? 'driver' : 'dealer';
    const inactiveState = INACTIVE_STATE_BY_OUTCOME[`${side}:${controlling.status}`] ?? null;
    if (inactiveState == null) return;

    // rejected / superseded sit on a driver counter, so surface the dealer offer beneath it;
    // the dealer-side states render themselves.
    const displayed = side === 'driver' ? parentOf(controlling) : controlling;
    if (displayed == null || displayed.amount <= 0) return;

    inactiveCards.push({
      offer: displayed,
      dealerLabel: orgName,
      amount: displayed.amount,
      driverCounterAmount: controlling.status === 'rejected' ? controlling.amount : null,
      inactiveState,
      inactiveAt: controlling.updatedAt,
      history,
    });
  });

  // Active bucket: pending dealer offers (A/B) above driver counters (C). Dealer offers sort by
  // soonest-expiring; driver counters (no TTL of their own) keep amount-descending below them.
  const dealerSide = activeCards
    .filter((card) => card.driverCounter == null)
    .sort(bySoonestExpiring);
  const driverCounters = activeCards
    .filter((card) => card.driverCounter != null)
    .sort(byAmountDescending);

  // Top offer badge tracks the highest amount regardless of expiry-sort position.
  const topOfferId =
    dealerSide.length >= 2
      ? dealerSide.reduce((highest, card) => (card.amount > highest.amount ? card : highest)).offer
          .id
      : null;

  const orderedActive: OfferCardView[] = [...dealerSide, ...driverCounters].map((card) => ({
    ...card,
    isTopOffer: card.offer.id === topOfferId,
  }));

  // Inactive cards sort by time — most-recently-inactive first.
  const orderedInactive = [...inactiveCards].sort(byMostRecentlyInactive);

  return {
    activeCards: orderedActive,
    inactiveCards: orderedInactive,
  };
};

export const hasVisibleOfferCards = (offers?: MarketplaceOffer[]): boolean => {
  const { activeCards, inactiveCards } = getOfferCards(offers);
  return activeCards.length > 0 || inactiveCards.length > 0;
};
